import { createTypes, completeTypes } from 'redux-recompose';
import { defaultFilters, menuMapper } from '@mercat-tech/prices';

import StoresService from '~services/Store/service';
import MenuService from '~services/Menu/service';
import { intl } from '~lib/intl';
import CartActions from '~redux/Cart/actions';

import { getDayLimit } from './getDayLimit';

const byHiddenProduct = () => (product) => {
  return !product.hidden;
};

export const actions = createTypes(
  completeTypes([
    'SET_CUSTOMER',
    'SET_DELIVERY_TYPE',
    'SET_STORE',
    'SET_STORE_DATA',
    'GET_STORE_BY_ID',
    'GET_MENU_BY_ID',
    'SET_DELIVERY_TIME',
    'SET_COUPON',
    'SET_PAYMENT_METHOD',
    'SET_PRODUCTS',
    'CLEAR_ORDER',
    'SET_ACTIVE_TAB',
    'SET_ADDRESS',
    'SET_BILLING',
    'SET_TAX_FREE'
  ]),
  '@@ORDER_TAKER'
);

export const targets = {
  ACTIVE_TAB: 'activeTab',
  CUSTOMER: 'customer',
  DELIVERY_TYPE: 'deliveryType',
  STORE: 'store',
  STORE_DATA: 'storeData',
  DELIVERY_TIME: 'deliveryTime',
  COUPON: 'coupon',
  PAYMENT_METHOD: 'paymentMethod',
  PRODUCTS: 'products',
  MENU: 'menu',
  ADDRESS: 'address',
  BILLING: 'billing',
  TAX_FREE: 'taxFree'
};

const actionCreators = {
  setActiveTab: ({ name }) => ({
    type: actions.SET_ACTIVE_TAB,
    target: targets.ACTIVE_TAB,
    payload: name
  }),
  setCustomer: (customer) => ({
    type: actions.SET_CUSTOMER,
    target: targets.CUSTOMER,
    payload: customer
  }),

  setDeliveryType: (type) => ({
    type: actions.SET_DELIVERY_TYPE,
    target: targets.DELIVERY_TYPE,
    payload: type
  }),

  setStore: (store) => (dispatch, getState) => {
    const prevStore = getState().orderTaker.store;
    dispatch({
      type: actions.SET_STORE,
      target: targets.STORE,
      payload: store
    });
    if (prevStore && prevStore !== store) {
      dispatch(CartActions.clearCart());
    }
  },

  setStoreData: (storeData) => ({
    type: actions.SET_STORE_DATA,
    target: targets.STORE_DATA,
    payload: storeData
  }),

  setDeliveryTime: (time) => ({
    type: actions.SET_DELIVERY_TIME,
    target: targets.DELIVERY_TIME,
    payload: time
  }),

  setCoupon: (coupon) => (dispatch, getState) => {
    const prevCoupon = getState().orderTaker?.coupon;
    dispatch({
      type: actions.SET_COUPON,
      target: targets.COUPON,
      payload: coupon
    });
    if (coupon !== prevCoupon) {
      dispatch(CartActions.checkCart());
    }
  },

  setTaxFree: (taxFree) => ({
    type: actions.SET_TAX_FREE,
    target: targets.TAX_FREE,
    payload: taxFree
  }),

  setPaymentMethod: (method) => ({
    type: actions.SET_PAYMENT_METHOD,
    target: targets.PAYMENT_METHOD,
    payload: method
  }),

  setProducts: (products) => ({
    type: actions.SET_PRODUCTS,
    target: targets.PRODUCTS,
    payload: products
  }),

  setAddress: (address) => ({
    type: actions.SET_ADDRESS,
    target: targets.ADDRESS,
    payload: address
  }),

  setBilling: (billing) => ({
    type: actions.SET_BILLING,
    target: targets.BILLING,
    payload: billing
  }),

  clearOrder: () => ({
    type: actions.CLEAR_ORDER
  }),

  setStoreAndFetchData: (store) => async (dispatch, getState) => {
    const state = getState();
    dispatch(actionCreators.setStore(store));

    try {
      const storeData = await StoresService.getStoreById(store);
      dispatch({
        type: actions.SET_STORE_DATA,
        target: targets.STORE_DATA,
        payload: storeData.data
      });
      const menuId = storeData.data.menuId;
      const { futureOrdersEnabled } = storeData.data;
      const dayLimit = getDayLimit(futureOrdersEnabled, state.core.settings);
      const { deliveryType } = state.orderTaker;
      if (menuId) {
        try {
          const menu = await MenuService.getMenu(menuId);
          const disabledProducts = await MenuService.getDisabledProducts(store);
          const filteredMenu = menuMapper({
            menuData: menu,
            disableds: disabledProducts,
            sortCategoryByKey: 'position',
            filters: [
              defaultFilters.byPlatform('web'),
              defaultFilters.byDispatchMethod(deliveryType),
              defaultFilters.byDeadlineAvailability(dayLimit.format(), intl.timezone),
              defaultFilters.byDisabledProductByShowAttribute(),
              byHiddenProduct()
            ]
          });
          dispatch({
            type: actions.GET_MENU_BY_ID,
            target: targets.MENU,
            payload: filteredMenu
          });
        } catch (err) {
          console.error('[OrderTaker] Error al obtener el menú:', err);
        }
      } else {
        console.warn('[OrderTaker] menuId no encontrado en storeData');
      }
    } catch (err) {
      console.error('[OrderTaker] Error al obtener store/menu:', err);
    }
  }
};

export default actionCreators;
